import { Link } from 'react-router-dom';
import { PiArrowRightBold, PiUploadSimpleBold, PiWarningBold } from 'react-icons/pi';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import type { CommissionMonthlyNf } from '@v2/pages/commissioning/types';
import { fmtBrl } from './homeFormat';

type Props = {
    selectedMonth: string;
    nfs: CommissionMonthlyNf[];
    isSelf: boolean;
    userId?: string;
};

export default function SellerNfPendencies({ selectedMonth, nfs, isSelf, userId }: Props) {
    const { t } = useI18n();

    const awaiting = nfs
        .filter((nf) => nf.status === 'awaiting_nf' && nf.period <= selectedMonth)
        .sort((a, b) => a.period.localeCompare(b.period));

    if (awaiting.length === 0) {
        return null;
    }

    const historyTo = userId
        ? `/commissioning/monthly-nfs?user_id=${userId}`
        : '/commissioning/monthly-nfs';

    return (
        <section className="space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-zinc-200">
                {t('v2.my_commission.pendencies_title')}
            </h2>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                {awaiting.map((nf) => {
                    const overdue = nf.period < selectedMonth;
                    const to = userId
                        ? `/commissioning/monthly-nfs/${nf.id}`
                        : `/my-commission/monthly-nf/${nf.id}`;
                    return (
                        <div
                            key={nf.id}
                            className={cn(
                                'flex flex-col justify-between rounded-2xl border p-4',
                                overdue
                                    ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30'
                                    : 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30',
                            )}
                        >
                            <div>
                                <div className="mb-1 flex items-center gap-1.5">
                                    {overdue && <PiWarningBold className="size-4 text-red-600" />}
                                    <p className={cn(
                                        'text-sm font-bold',
                                        overdue ? 'text-red-900 dark:text-red-100' : 'text-amber-900 dark:text-amber-100',
                                    )}>
                                        {overdue
                                            ? t('v2.my_commission.nf_overdue_title', { period: nf.period_label })
                                            : t('v2.my_commission.nf_available_title', { period: nf.period_label })}
                                    </p>
                                </div>
                                <p className={cn(
                                    'text-xs',
                                    overdue ? 'text-red-700/80 dark:text-red-200/80' : 'text-amber-800/80 dark:text-amber-200/80',
                                )}>
                                    {overdue
                                        ? t('v2.my_commission.nf_overdue_hint')
                                        : t('v2.my_commission.nf_available_hint', {
                                            amount: fmtBrl(Number(nf.total_commissions || nf.total_amount || 0)),
                                            count: String(nf.records_count || 0),
                                        })}
                                </p>
                            </div>
                            {isSelf && (
                                <Link
                                    to={to}
                                    className={cn(
                                        'mt-4 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-white',
                                        overdue ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700',
                                    )}
                                >
                                    <PiUploadSimpleBold className="size-4" />
                                    {overdue
                                        ? t('v2.my_commission.nf_send_now')
                                        : t('v2.my_commission.nf_send')}
                                </Link>
                            )}
                        </div>
                    );
                })}
            </div>
            <Link
                to={historyTo}
                className="inline-flex items-center gap-1 text-sm font-medium text-orange-700 hover:underline dark:text-orange-400"
            >
                {t('v2.my_commission.nf_history_link')}
                <PiArrowRightBold className="size-3.5" />
            </Link>
        </section>
    );
}
